import React, {Component} from "react"
import Papa from "papaparse"
import moment from "moment"

import {injectIntl, InjectIntl} from "@irati/utils/lang"
import adminReportingService from "@irati/adminReporting/services/adminReporting"
import authentication from "@irati/auth"
import makeCSV from "@irati/utils/csv/makeCSV"

import firstnameLastname from "@irati/identity/helpers/user/firstnameLastname"
import teamsService from "@irati/identity/services/teams"
import {User} from "@irati/types"
import toTeamName from "@irati/identity/utils/team/teamName"
import {ALL_TEAMS} from "../../../Region"
import DownloadButton from "../Download"

interface Props extends InjectIntl {
  showButtons: boolean
  onDownload?: () => void
  hideText?: boolean
}

interface State {
  load: Promise<void> | null
}

class MostPopularPollsDownload extends Component<Props, State> {
  state = {
    load: null
  }

  getTeams = async (users: User[]) => {
    const teamNames = await Promise.all(
      users.map(async (user) => {
        const teamIds = user?.teamIds || []
        const teams = await Promise.all(
          teamIds.map((teamId) => {
            return teamsService.get({tenantId: authentication.business().uuid, teamId})
          })
        )
        return teams.filter(({teamName}) => teamName !== "$$TEAM_ROOT$$").map(toTeamName)
      })
    )
    return teamNames
  }

  downloadReport = () => {
    const {formatMessage, regionId, duration, category} = this.props

    const regionIds = regionId !== ALL_TEAMS ? [regionId] : undefined

    const load = adminReportingService
      .getCelebrationsReport({
        businessId: authentication.business().uuid,
        regionIds,
        duration,
        category
      })
      .then(async ({polls}) => {
        const headers = [
          formatMessage({id: "admin.reporting.celebrations.polls.mostPopular.headers.title"}),
          formatMessage({id: "admin.reporting.celebrations.polls.mostPopular.headers.createdDate"}),
          formatMessage({id: "admin.reporting.celebrations.polls.mostPopular.headers.authorName"}),
          formatMessage({id: "admin.reporting.celebrations.polls.mostPopular.headers.voterName"}),
          formatMessage({id: "admin.reporting.celebrations.polls.mostPopular.headers.voterTeam"}),
          formatMessage({id: "admin.reporting.celebrations.polls.mostPopular.headers.nominationComment"}),
          formatMessage({id: "admin.reporting.celebrations.polls.mostPopular.headers.nominationName"}),
          formatMessage({id: "admin.reporting.celebrations.polls.mostPopular.headers.nominationTeam"}),
          formatMessage({id: "admin.reporting.celebrations.polls.mostPopular.headers.winner"})
        ]

        const data = await Promise.all(
          polls.polls.map(async (p) => {
            const winnerIds = p.poll.winners.results.map((winner) => winner.user.uuid)
            const winnerKeys = p.poll.votes.results
              .map((vote) =>
                winnerIds.find((winnerId) => winnerId === vote.user.uuid)
                  ? formatMessage({id: "admin.reporting.celebrations.polls.mostPopular.values.winner"})
                  : formatMessage({id: "admin.reporting.celebrations.polls.mostPopular.values.nonWinner"})
              )
              .join("\n")

            const votedTeamPromise = this.getTeams(p.poll.votes.results.map((vote) => vote.createdBy))
            const nominatedTeamPromise = this.getTeams(p.poll.votes.results.map((vote) => vote.user))
            return await Promise.all([votedTeamPromise, nominatedTeamPromise]).then(([votedTeams, nominatedTeams]) => {
              return [
                p.title,
                moment.unix(p.createdAt.seconds).format("DD/MM/YYYY"),
                firstnameLastname(p.poll.createdBy),
                p.poll.votes.results.map((vote) => firstnameLastname(vote.createdBy)).join("\n"),
                votedTeams.join("\n"),
                p.poll.votes.results.map((vote) => vote.message).join("\n"),
                p.poll.votes.results.map((vote) => firstnameLastname(vote.user)).join("\n"),
                nominatedTeams.join("\n"),
                formatMessage({id: winnerKeys})
              ]
            })
          })
        )
        const csv = Papa.unparse({fields: headers, data})
        makeCSV(csv, "mostPopularPolls.csv")
      })

    this.setState({load})
  }

  render() {
    const {load} = this.state
    const {hideText} = this.props

    return (
      <DownloadButton
        hideText={hideText}
        tooltip="admin.reporting.celebrations.polls.mostPopular.tooltip"
        load={load}
        onClick={this.downloadReport}
      />
    )
  }
}

export default injectIntl(MostPopularPollsDownload)
